import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import type { Group, Role, User, UserGroupRoles } from '@esg/esg-global-types';
import type { CreateUserClientData } from '@esg/syntiro-api-core';

import { createUser } from '../../graphql/services/user.service';
import { sendNewUserWelcomeEmail } from '../../lib/user_management/welcome_email';
import { generateUuidV4 } from '../../util/uuid';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';
import GroupCompanyAccessPicker from './shared/GroupCompanyAccessPicker';
import { buildGroupsPayload, hasAnyCompany, type AccessSelection } from './shared/access_selection';
import { isEmailTaken, normaliseUserEmail, validEmailRe } from './shared/email_validation';

interface AddUserDrawerProps {
  groups: Group[];
  roles: Role[];
  /**
   * The signed-in user. Only a super admin may grant super-admin access, so the
   * toggle is hidden from group admins (mirrors {@link EditUserAccessTab}).
   */
  currentUser: User | null;
  /** Called with the new user after a successful create. */
  onCreated: (created: User) => void;
  onClose: () => void;
}

interface DraftForm {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  type: User['type'];
  /** Companies selected per group — spans as many groups as the admin manages. */
  selection: AccessSelection;
  role: string;
  super_admin: boolean;
  /**
   * Whether to send the invitation set-up email on create. Off creates the user
   * without contacting them; the invite is sent later from the list's "Resend
   * invite". Mirrors the bulk import's "Send welcome emails" switch.
   */
  send_invite: boolean;
}

const INITIAL: DraftForm = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  type: 'internal',
  selection: {},
  role: 'viewer',
  super_admin: false,
  send_invite: true
};

const validPhone = (phone: string): boolean =>
  phone === '' || (/^\+?\d+$/.test(phone) && phone.length >= 4 && phone.length <= 17);

/**
 * Stable string form of a draft for the unsaved-changes comparison. The access
 * selection is a keyed object that gains empty entries as groups are expanded
 * and toggled, so a plain `JSON.stringify` would report a draft as dirty after
 * a select-then-deselect that left no access chosen. Dropping empty groups and
 * sorting both the group keys and the company ids makes the comparison depend
 * on the access actually chosen, not the order it was clicked in.
 */
const draftFingerprint = (draft: DraftForm): string => {
  const selection = Object.entries(draft.selection)
    .filter(([, entry]) => entry.companyIds.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupId, entry]) => [groupId, [...entry.companyIds].sort()] as const);
  return JSON.stringify({ ...draft, selection });
};

/** Constant baseline for the dirty check — INITIAL never changes. */
const INITIAL_FINGERPRINT: string = draftFingerprint(INITIAL);

/** Single-form drawer for creating a user and sending their invitation. */
const AddUserDrawer = ({ groups, roles, currentUser, onCreated, onClose }: AddUserDrawerProps) => {
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const isSuperAdmin = !!currentUser?.super_admin;
  const [draft, setDraft] = useState<DraftForm>(INITIAL);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [emailExists, setEmailExists] = useState<boolean>(false);

  const dirty = useMemo(() => draftFingerprint(draft) !== INITIAL_FINGERPRINT, [draft]);
  const guard = useUnsavedChangesGuard({ dirty, onDiscard: onClose });

  // The address as it will actually be sent and stored. Validating, creating
  // and addressing the welcome email all read this rather than the raw field,
  // so a pasted address with surrounding whitespace is cleaned up instead of
  // being rejected as malformed — the same normalise-then-validate order the
  // edit path and the API use.
  const normalisedEmail: string = normaliseUserEmail(draft.email);

  // Super admins have implicit access to every group and company, so they don't
  // need a group / company / role selection to be valid.
  const isValidUserDetails =
    validEmailRe.test(normalisedEmail) &&
    !emailExists &&
    draft.first_name.trim().length > 0 &&
    draft.last_name.trim().length > 0 &&
    validPhone(draft.phone) &&
    (draft.super_admin || hasAnyCompany(draft.selection));

  const patch = (partial: Partial<DraftForm>) => setDraft((cur) => ({ ...cur, ...partial }));

  /**
   * Checks the entered email against existing users (case-insensitive, incl.
   * soft-deleted) so a duplicate is caught before submit. The backend
   * createUser also rejects duplicates; this is the pre-submit UX guard.
   */
  const checkEmailAvailability = async (): Promise<boolean> => {
    const exists: boolean = await isEmailTaken(normalisedEmail);
    setEmailExists(exists);
    return exists;
  };

  const handleSubmit = async () => {
    if (!isValidUserDetails || !group || !company) return;
    setSubmitting(true);
    try {
      // Re-check before creating: a fast Submit click fires blur then submit
      // before the blur's async check resolves, so `emailExists` may be stale.
      if (await checkEmailAvailability()) return;
      const newId = generateUuidV4();
      // Super admins get no group/company membership — their access is implicit.
      // The manageable group ids bound the payload, so a selection key the
      // picker can no longer render cannot smuggle in an unseen access grant.
      const groupsPayload: UserGroupRoles = draft.super_admin
        ? {}
        : buildGroupsPayload(
            draft.selection,
            draft.role,
            groups.map((groupOption) => groupOption.id)
          );
      const result = await createUser(
        { group_id: group.id, company_id: company.id },
        {
          id: newId,
          email: normalisedEmail,
          first_name: draft.first_name,
          last_name: draft.last_name,
          phone: draft.phone,
          super_admin: draft.super_admin,
          groups: groupsPayload,
          type: draft.type
        } as CreateUserClientData,
        { return_data: true, create_audit_log: true }
      );
      if (result) {
        if (draft.send_invite) {
          try {
            await sendNewUserWelcomeEmail(
              { name: draft.first_name, email: normalisedEmail },
              'AddUser'
            );
            setFeedbackData({
              message: `Invitation sent to ${normalisedEmail}`,
              state: true,
              type: 'success'
            });
          } catch {
            setFeedbackData({
              message: `User created, but the invitation email to ${normalisedEmail} failed to send — use "Reissue invite" to retry.`,
              state: true,
              type: 'warning'
            });
          }
        } else {
          setFeedbackData({
            message: `${normalisedEmail} created without an invitation — send it later with "Resend invite".`,
            state: true,
            type: 'success'
          });
        }
        onCreated(result as User);
        onClose();
      } else {
        setFeedbackData({
          message: 'Failed to create user',
          state: true,
          type: 'error'
        });
      }
    } catch (err: unknown) {
      // createUser throws a MetadataError on any server-side rejection — most
      // reachably ENTITY_QUOTA_EXCEEDED, since every selected company consumes
      // its own subscription seat and one full company fails the whole create.
      // Without this the rejection escaped as an unhandled promise and the
      // drawer simply sat there: no snackbar, no error, nothing to act on.
      setFeedbackData({
        message: err instanceof Error ? err.message : 'Failed to create user',
        state: true,
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Add user
        </Typography>
        <IconButton onClick={() => guard.requestClose()} aria-label="Close drawer">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Stack spacing={2} sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Identity
          </Typography>
          <TextField
            label="Email"
            value={draft.email}
            onChange={(e) => {
              // Clear a stale "already exists" flag while the user edits.
              if (emailExists) setEmailExists(false);
              patch({ email: e.target.value });
            }}
            onBlur={checkEmailAvailability}
            error={
              (normalisedEmail.length > 0 && !validEmailRe.test(normalisedEmail)) || emailExists
            }
            helperText={
              emailExists
                ? 'A user with this email address already exists.'
                : draft.send_invite
                  ? 'An invitation set-up link will be sent to this address.'
                  : 'No email will be sent — the invitation can be issued later.'
            }
            required
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="First name"
              value={draft.first_name}
              onChange={(e) => patch({ first_name: e.target.value })}
              required
            />
            <TextField
              label="Last name"
              value={draft.last_name}
              onChange={(e) => patch({ last_name: e.target.value })}
              required
            />
          </Box>
          <TextField
            label="Phone (optional)"
            value={draft.phone}
            onChange={(e) => patch({ phone: e.target.value })}
            error={!validPhone(draft.phone)}
            helperText={!validPhone(draft.phone) ? 'Digits only, e.g. +27215550100' : undefined}
          />
          <TextField
            select
            label="User type"
            value={draft.type}
            onChange={(e) => patch({ type: e.target.value as User['type'] })}
          >
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="external">External</MenuItem>
            <MenuItem value="any">Combination</MenuItem>
          </TextField>

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Access
          </Typography>
          {isSuperAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={draft.super_admin}
                  onChange={(e) => patch({ super_admin: e.target.checked })}
                />
              }
              label="Super admin (full system access)"
            />
          )}

          {draft.super_admin ? (
            <Typography variant="body2" color="text.secondary">
              Super admins have access to every group and company, so no group, company or role
              selection is needed.
            </Typography>
          ) : (
            <GroupCompanyAccessPicker
              groups={groups}
              roles={roles}
              value={draft.selection}
              onChange={(selection) => patch({ selection })}
              role={draft.role}
              onRoleChange={(role) => patch({ role })}
            />
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Invitation
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={draft.send_invite}
                onChange={(e) => patch({ send_invite: e.target.checked })}
              />
            }
            label="Send invitation email"
          />
          {!draft.send_invite && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              The user will be created without an invitation email. Use &quot;Resend invite&quot;
              from the list later when you&apos;re ready to send it.
            </Typography>
          )}
        </Stack>
      </Box>

      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 2,
          bgcolor: 'background.paper'
        }}
      >
        <Button onClick={() => guard.requestClose()}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={draft.send_invite ? <SendIcon /> : <PersonAddIcon />}
          disabled={!isValidUserDetails || submitting}
          onClick={handleSubmit}
        >
          {draft.send_invite
            ? submitting
              ? 'Sending…'
              : 'Send invitation'
            : submitting
              ? 'Creating…'
              : 'Create user'}
        </Button>
      </Box>

      <Dialog open={guard.isModalOpen} onClose={guard.cancelDiscard}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Closing this drawer will lose the user details you&apos;ve entered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.cancelDiscard}>Keep editing</Button>
          <Button color="error" onClick={guard.confirmDiscard}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddUserDrawer;
